import React, { useCallback, useContext, useEffect, useRef } from "react";

import { SurveyContext } from "../../providers/survey";
import { SurveySplashDataContext } from "../../providers/survey-splash-data";
import { SurveyQuestionsVisibilityContext } from "../../providers/survey-questions-visibility";
import { SurveyValidInputContext } from "../../providers/survey-valid-input";

import { isValidQuestion } from "../../utils/survey/valid-input";
import { getAnswer } from "../../utils/survey/answers";

import QuestionLeadingText from "./leading-text";
import QuestionDescription from "./description";
import QuestionReferenceImage from "./reference-image";
import QuestionFormField from "./form-field";
import QuestionInvalidInputMessage from "./invalid-input-message";

const SurveyQuestion = ({ questionIndex, dimensionIndex, question }) => {
  const {
    answers,
    updateAnswersOnMarkedAnswer,
    updateAnswersOnDeletedAnswer,
    updateAnswersOnHiddenQuestion,
  } = useContext(SurveyContext);
  const { questionsVisibility, updateQuestionsVisibility } = useContext(
    SurveyQuestionsVisibilityContext
  );
  const { validInput, firstInvalidQuestion } = useContext(SurveyValidInputContext);
  const { surveyMetadata } = useContext(SurveySplashDataContext);

  const { surveyRoute } = surveyMetadata;

  const questionRef = useRef();

  const isVisibleQuestion = questionsVisibility?.[dimensionIndex]?.[questionIndex]?.["visible"];
  const isValid = isValidQuestion(validInput, dimensionIndex, questionIndex);

  const isFirstInvalidQuestion =
    firstInvalidQuestion?.dimensionIndex === dimensionIndex &&
    firstInvalidQuestion?.questionIndex === questionIndex;

  const defaultAnswer = getAnswer(answers, dimensionIndex, questionIndex, question.question_type);

  const scrollToFirstInvalidInput = useCallback(() => {
    if (isFirstInvalidQuestion) {
      window.scrollTo({
        behavior: "smooth",
        top: questionRef.current.getBoundingClientRect().top + window.scrollY - 75,
      });
    }
  }, [isFirstInvalidQuestion]);

  useEffect(() => {
    scrollToFirstInvalidInput();
  }, [scrollToFirstInvalidInput, validInput]);

  const deleteAnswerOnToggledToHidden = useCallback(() => {
    if (defaultAnswer && !isVisibleQuestion) {
      updateAnswersOnHiddenQuestion(dimensionIndex, questionIndex);
    }
  }, [isVisibleQuestion]);

  useEffect(() => {
    deleteAnswerOnToggledToHidden();
  }, [deleteAnswerOnToggledToHidden]);

  const handleChange = (event) => {
    if (event.target.value === "" || (event.target.type === "checkbox" && !event.target.checked)) {
      updateAnswersOnDeletedAnswer(dimensionIndex, questionIndex, question, event.target);
    } else {
      updateAnswersOnMarkedAnswer(dimensionIndex, questionIndex, question, event.target);
    }
    updateQuestionsVisibility(dimensionIndex, questionIndex);
  };

  return (
    isVisibleQuestion && (
      <div
        ref={questionRef}
        className={`card survey-question ${isValid ? "" : "has-invalid-input"}`}
      >
        {question.question_name ? (
          <div className="card-header">{`${questionIndex + 1}.- ${question.question_name}`}</div>
        ) : (
          <div className="question-number">
            <div></div>
            <span>{questionIndex + 1}</span>
          </div>
        )}
        <div className={`card-body ${!question.question_name && "mt-2"}`}>
          <QuestionLeadingText questionType={question.question_type} {...question} />
          {question.question_description && (
            <QuestionDescription description={question.question_description} />
          )}
          {question.has_reference_image && (
            <QuestionReferenceImage
              surveyRoute={surveyRoute}
              dimensionIndex={dimensionIndex}
              questionIndex={questionIndex}
            />
          )}
          <QuestionFormField
            questionType={question.question_type}
            questionDetails={{
              dimensionIndex: dimensionIndex,
              questionIndex: questionIndex,
              defaultAnswer: defaultAnswer,
              handleChange: (event) => handleChange(event),
              ...question,
            }}
          />
        </div>
        {!isValid && <QuestionInvalidInputMessage questionType={question.question_type} />}
      </div>
    )
  );
};

export default SurveyQuestion;
